import { create } from 'zustand';
import api from '../services/api';

/**
 * Feature Flag Store
 * Manages feature flags for the current user's company — and, since Phase
 * 7a, what the tenant's plan allows:
 *
 *   - `flags`: key → effective value (a locked flag always reads false);
 *   - `entitlements`: the plan, the trial date, the modules and add-ons held,
 *     and `locked`, the flag keys the plan does not include;
 *   - `permissionOf`: flag key → the permission internal staff need. Served
 *     by the backend so that there is exactly one copy of that map; the copy
 *     this file's neighbour App.jsx used to carry was stale and failed open.
 */
const EMPTY_ENTITLEMENTS = Object.freeze({
  plan: null,
  effectivePlan: null,
  source: 'none',
  trialEndsAt: null,
  modules: [],
  addons: [],
  locked: [],
});

export const useFeatureFlagStore = create((set, get) => ({
  flags: {},
  entitlements: EMPTY_ENTITLEMENTS,
  permissionOf: {},
  isLoaded: false,
  isLoading: false,
  error: null,

  /**
   * Load feature flags from the API
   */
  loadFlags: async () => {
    // Prevent duplicate loading
    if (get().isLoading) return;

    set({ isLoading: true, error: null });

    try {
      const response = await api.get('/feature-flags/my-flags');
      set({
        flags: response.data.flags || {},
        entitlements: { ...EMPTY_ENTITLEMENTS, ...(response.data.entitlements || {}) },
        permissionOf: response.data.permissionOf || {},
        isLoaded: true,
        isLoading: false
      });
    } catch (error) {
      console.error('Failed to load feature flags:', error);
      set({
        error: error.message,
        isLoaded: true,
        isLoading: false
      });
    }
  },

  /**
   * Read the flags again after one was saved. Not through the loading state,
   * which makes FeatureRoute swap the page for a spinner and lose what it held.
   * A failed read leaves the flags as they were.
   */
  refreshFlags: async () => {
    try {
      const response = await api.get('/feature-flags/my-flags');
      set({
        flags: response.data.flags || {},
        entitlements: { ...EMPTY_ENTITLEMENTS, ...(response.data.entitlements || {}) },
        permissionOf: response.data.permissionOf || {},
      });
    } catch (error) {
      console.warn('Feature flags could not be refreshed:', error.message);
    }
  },

  /**
   * Check if a feature is enabled
   * @param {string} key - Feature flag key
   * @returns {boolean}
   */
  isEnabled: (key) => {
    const { flags } = get();
    const value = flags[key];
    return value === true || value === 'true';
  },

  /** Whether the plan leaves this flag switched off for good. */
  isLocked: (key) => get().entitlements.locked.includes(key),

  /** Whether the plan includes a catalogue module (`time`, `whitelabel`, …). */
  hasModule: (moduleKey) => get().entitlements.modules.includes(moduleKey),

  /**
   * Get the value of a feature flag
   * @param {string} key - Feature flag key
   * @returns {any}
   */
  getValue: (key) => {
    return get().flags[key] ?? null;
  },

  /**
   * Check multiple flags at once
   * @param {string[]} keys - Array of feature flag keys
   * @returns {Object} - Map of key to boolean
   */
  checkFlags: (keys) => {
    const { flags } = get();
    const result = {};
    keys.forEach(key => {
      const value = flags[key];
      result[key] = value === true || value === 'true';
    });
    return result;
  },

  /**
   * Reset the store (e.g., on logout)
   */
  reset: () => {
    set({
      flags: {},
      entitlements: EMPTY_ENTITLEMENTS,
      permissionOf: {},
      isLoaded: false,
      isLoading: false,
      error: null
    });
  }
}));

/**
 * Hook for checking if a single feature is enabled
 * @param {string} key - Feature flag key
 * @returns {boolean}
 */
export function useFeatureFlag(key) {
  return useFeatureFlagStore(state => {
    const value = state.flags[key];
    return value === true || value === 'true';
  });
}

/**
 * Hook for checking multiple features
 * @param {string[]} keys - Array of feature flag keys
 * @returns {Object} - Map of key to boolean
 */
export function useFeatureFlags(keys) {
  return useFeatureFlagStore(state => {
    const result = {};
    keys.forEach(key => {
      const value = state.flags[key];
      result[key] = value === true || value === 'true';
    });
    return result;
  });
}

/**
 * Hook for getting feature flag loading state
 * @returns {{ isLoaded: boolean, isLoading: boolean, error: string | null }}
 */
export function useFeatureFlagStatus() {
  return useFeatureFlagStore(state => ({
    isLoaded: state.isLoaded,
    isLoading: state.isLoading,
    error: state.error
  }));
}

/** The plan and add-ons the tenant holds, as the backend described them. */
export function useEntitlements() {
  return useFeatureFlagStore((state) => state.entitlements);
}

/** True when the plan does not include the module a flag belongs to. */
export function useFlagLocked(key) {
  return useFeatureFlagStore((state) => state.entitlements.locked.includes(key));
}

/** True when the plan includes a catalogue module (`time`, `whitelabel`, `sso`, …). */
export function useModuleEntitled(moduleKey) {
  return useFeatureFlagStore((state) => state.entitlements.modules.includes(moduleKey));
}

/** The flag → permission map internal staff are gated on. */
export function usePermissionOf() {
  return useFeatureFlagStore((state) => state.permissionOf);
}
